import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { GroupedSwPartNumber } from '../../../../../shared/models/sw-part-number/grouped-sw-part-number';
import { NwNumberMajor } from '../../../../../shared/models/sw-part-number/nw-number-major';
import { DatePipe, NgClass } from '@angular/common';
import { DeleteIconComponent } from '../../../icons/delete-icon/delete-icon.component';
import { AddComponent } from '../../../icons/add/add.component';
import { Policy } from '../../../../../shared/models/info/policy';
import { IfPolicySatisfiedDirective } from '../../../../directives/if-policy-satisfied.directive';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { NoteIconComponent } from '../../../icons/note-icon/note-icon.component';
import { SwPartNumberState } from '../../../../../shared/enums/sw-part-number-state';
import { RefreshIconComponent } from '../../../icons/refresh-icon/refresh-icon.component';
import { CheckComponent } from '../../../icons/check/check.component';
import { SnapshotMinorReleaseComponent } from '../../../common/inputs/snapshot-minor-release/snapshot-minor-release.component';
import { RocketIconComponent } from '../../../icons/rocket-icon/rocket-icon.component';
import { UploadFileComponent } from '../../../common/inputs/upload-file/upload-file.component';
import { CommonUtils } from '../../../../common/common-utils';
import { CollapsableNodeStateStoreService } from '../../../../services/collapsable-node-state-store.service';
import { ChevronDoubleDownComponent } from '../../../icons/chevron-double-down-icon/chevron-double-down-icon.component';
import { ChevronDoubleUpIconComponent } from '../../../icons/chevron-double-up-icon/chevron-double-up-icon.component';
import { ChevronIconComponent } from '../../../icons/table/chevron-icon/chevron-icon.component';
import { ARROWS } from '../../../../../shared/constants/arrows';
import { PreselectionService } from '../../../../services/preselection.service';
import { PreselectionType } from '../../../../../shared/enums/preselection-type';
import { TranslationImportReporterComponent } from '../../../common/reporter/translation-import-reporter/translation-import-reporter.component';
import { CancelIconComponent } from '../../../icons/cancel-icon/cancel-icon.component';
import { SwPartNumber } from '../../../../../shared/models/sw-part-number/sw-part-number';
import { ImportReport } from '../../../../../shared/models/report/import-report';
import { BranchIcon } from '../../../icons/branch-icon/branch-icon';
import { ImportType } from './import-type';
import { ExportPopover } from '../../../sw-part-number/popover/export-popover/export-popover';
import { ImportPopover } from '../../../sw-part-number/popover/import-popover/import-popover';

@Component({
  selector: 'app-sw-part-number-accordion',
  imports: [
    NgClass,
    DatePipe,
    DeleteIconComponent,
    AddComponent,
    IfPolicySatisfiedDirective,
    AgentActionDirective,
    NoteIconComponent,
    RefreshIconComponent,
    CheckComponent,
    SnapshotMinorReleaseComponent,
    RocketIconComponent,
    UploadFileComponent,
    CheckComponent,
    ChevronDoubleDownComponent,
    ChevronDoubleUpIconComponent,
    ChevronIconComponent,
    TranslationImportReporterComponent,
    CancelIconComponent,
    BranchIcon,
    ExportPopover,
    ImportPopover,
  ],
  templateUrl: './sw-part-number-accordion.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sw-part-number-accordion.component.scss',
})
export class SwPartNumberAccordionComponent implements OnChanges {
  @Input() public groupedSwPartNumbers?: GroupedSwPartNumber[];
  @Input() public selectedSwPartNumberId?: string;
  /**
   * Report data for displaying translation import results.
   * If provided, the report will be shown for the corresponding software part number.
   */
  @Input() public report?: { swPartNumberId: string; importReport: ImportReport };
  /**
   * Emitted when a software part number is clicked.
   * The emitted value is the ID of software part number.
   */
  @Output() public swPartNumberClicked = new EventEmitter<string>();
  @Output() public createSnapshotClicked = new EventEmitter<{
    sourceSwPartNumber: SwPartNumber;
    minorVersion: number;
  }>();
  @Output() public createNewBranchClicked = new EventEmitter<SwPartNumber>();
  @Output() public swPartNumberSoftDeleteClicked = new EventEmitter<SwPartNumber>();
  @Output() public updateSnapshotClicked = new EventEmitter<SwPartNumber>();
  @Output() public releaseSnapshotClicked = new EventEmitter<SwPartNumber>();
  @Output() public swPartNumberDownloadClicked = new EventEmitter<SwPartNumber>();
  @Output() public swPartNumberDocDownloadClicked = new EventEmitter<SwPartNumber>();
  @Output() public resetDocumentationExportedClicked = new EventEmitter<SwPartNumber>();
  @Output() public publishEventsClicked = new EventEmitter<SwPartNumber>();
  @Output() public resetOnTerminalExportedClicked = new EventEmitter<SwPartNumber>();
  @Output() public swPartNumberImportClicked = new EventEmitter<{
    swPartNumber: SwPartNumber;
    xmlFiles: File[];
    importType: ImportType;
  }>();
  /**
   * Emitted when the error memory sidebar for a specific NW number should be opened.
   * The emitted value is the ID of the NW number's error memory.
   */
  @Output() public nwNumberErrorMemoryClicked = new EventEmitter<string>();

  public readonly activeElements = new Map<string, boolean>();
  public readonly policy = Policy;
  public readonly swPartNumberState = SwPartNumberState;
  public readonly commonUtils = CommonUtils;
  public swPartNumberForSnapshot?: SwPartNumber;
  public minorVersionForSnapshot?: number;
  public swPartNumberForUpload?: SwPartNumber;
  public xmlFiles?: File[];
  public arrows = ARROWS;
  public importTypeEnum = ImportType;

  private readonly collapsableStateStore = inject(CollapsableNodeStateStoreService);
  private preselectionService = inject(PreselectionService);
  private settingsKeyId = 'SwPartNumberAccordion';
  private expandableElementsRegex = /NW\d{3}(-[A-Z]{1-2})?/;
  private importType = ImportType.ImportTerminalTranslation;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['groupedSwPartNumbers']) {
      this.initActiveElements();
    }
  }

  public isActive(element: string): boolean {
    return this.activeElements.get(element) || false;
  }

  public isSelected(swPartNumberId: string | undefined): boolean {
    return this.selectedSwPartNumberId === swPartNumberId;
  }

  public toggleActive(element: string): void {
    const currentState = this.activeElements.get(element) || false;
    this.setElementFoldingState(element, !currentState);
  }

  public expandAll(): void {
    this.setAllElementFoldingStates(true);
  }

  public collapseAll(): void {
    this.setAllElementFoldingStates(false);
  }

  public onSwPartNumberClicked(swPartNumberId: string): void {
    this.swPartNumberClicked.emit(swPartNumberId);
  }

  public getGroupedNwNumbers(): string[] {
    const allNwNumbers =
      this.groupedSwPartNumbers?.flatMap((group) =>
        group.swPartNumbers!.map((swPartNumber) => swPartNumber.nwNumber!)
      ) || [];
    return Array.from(new Set(allNwNumbers));
  }

  public getMajorParts(nwNumber: string): NwNumberMajor[] {
    const grouped = this.groupedSwPartNumbers?.filter((group) =>
      group.swPartNumbers?.some((part) => part.nwNumber === nwNumber)
    );
    return grouped?.flatMap((group) => group.nwNumberMajorParts || []) || [];
  }

  public getSwPartNumbers(nwNumberWithMajorRelease: string): SwPartNumber[] {
    const groupedSwPartNumber = this.groupedSwPartNumbers?.find((group) =>
      group.nwNumberMajorParts?.some((part) => part.nwNumberWithMajorRelease === nwNumberWithMajorRelease)
    );

    return (
      groupedSwPartNumber?.swPartNumbers?.sort((a, b) =>
        a.number!.replace('000', '999') < b.number!.replace('000', '999') ? 1 : -1
      ) || []
    );
  }

  public toggleEnterSnapshotMinorVersion(swPartNumber: SwPartNumber): void {
    this.swPartNumberForSnapshot = this.swPartNumberForSnapshot?.id === swPartNumber.id ? undefined : swPartNumber;
  }

  public snapshotMinorReleaseSelected(minorRelease: number): void {
    this.minorVersionForSnapshot = minorRelease;
  }

  public triggerCreateSnapshot(): void {
    this.createSnapshotClicked.emit({
      sourceSwPartNumber: this.swPartNumberForSnapshot!,
      minorVersion: this.minorVersionForSnapshot!,
    });
    this.swPartNumberForSnapshot = undefined;
    this.minorVersionForSnapshot = undefined;
  }

  public setUpload(swPartNumber: SwPartNumber, importType: ImportType): void {
    this.swPartNumberForUpload = this.swPartNumberForUpload?.id === swPartNumber.id ? undefined : swPartNumber;
    this.importType = importType;
  }

  public onFilesSelected(files: File[]): void {
    this.xmlFiles = files;
  }

  public uploadXmlFile(): void {
    this.swPartNumberImportClicked.emit({
      swPartNumber: this.swPartNumberForUpload!,
      xmlFiles: this.xmlFiles!,
      importType: this.importType,
    });
    this.swPartNumberForUpload = undefined;
    this.xmlFiles = undefined;
  }

  public closeReport(): void {
    this.report = undefined;
  }

  public openNwNumberErrorMemorySidebar(nwNumber: string): void {
    const nwNumberErrorMemoryId = this.getNwNumberErrorMemoryId(nwNumber);
    this.selectedSwPartNumberId = nwNumberErrorMemoryId;
    this.nwNumberErrorMemoryClicked.emit(nwNumberErrorMemoryId);
  }

  private getNwNumberErrorMemoryId(nwNumber: string): string | undefined {
    const swPartNumbers = this.groupedSwPartNumbers?.flatMap((group) => group.swPartNumbers || []);
    const swPartNumber = swPartNumbers?.find((spn) => spn.nwNumber === nwNumber);
    return swPartNumber?.nwNumberErrorMemoryId;
  }

  private setElementFoldingState(element: string, expanded: boolean): void {
    this.activeElements.set(element, expanded);
    this.collapsableStateStore.updateSingleState(this.settingsKeyId, element, expanded);
  }

  private setAllElementFoldingStates(expanded: boolean): void {
    for (const key of this.activeElements.keys()) {
      if (this.expandableElementsRegex.test(key)) {
        this.setElementFoldingState(key, expanded);
      }
    }
  }

  private initActiveElements(): void {
    const nwNumbers = this.getGroupedNwNumbers();
    const nwNumberMajorParts = nwNumbers
      .map((nwNumber) => ({
        majorParts: this.getMajorParts(nwNumber),
      }))
      .flatMap((item) => item.majorParts.map((part) => part.nwNumberWithMajorRelease!));
    const swPartNumber = nwNumberMajorParts
      .map((nwNumberWithMajorRelease) => this.getSwPartNumbers(nwNumberWithMajorRelease))
      .flatMap((swPartNumbers) => swPartNumbers.map((swPartNumber) => swPartNumber.number!));

    this.activeElements.clear();
    nwNumbers.forEach((nwNumber) => {
      this.activeElements.set(nwNumber, false);
    });
    nwNumberMajorParts.forEach((nwNumberWithMajorRelease) => {
      this.activeElements.set(nwNumberWithMajorRelease, false);
    });
    swPartNumber.forEach((swPart) => {
      this.activeElements.set(swPart, false);
    });

    this.collapsableStateStore.getStoredStates(this.activeElements, this.settingsKeyId);

    if (this.groupedSwPartNumbers && this.groupedSwPartNumbers?.length > 0) {
      const preselectedSwPartNumber = this.preselectionService.getIdAndClear(PreselectionType.SwPartNumber);
      if (preselectedSwPartNumber) {
        let swPartNumber: SwPartNumber | null = null;
        for (const group of this.groupedSwPartNumbers) {
          for (const spn of group.swPartNumbers ?? []) {
            if (spn.id == preselectedSwPartNumber) {
              swPartNumber = spn;
              break;
            }
          }
          if (swPartNumber) {
            break;
          }
        }

        if (swPartNumber) {
          this.setElementFoldingState(swPartNumber.nwNumber!, true);
          this.setElementFoldingState(swPartNumber.nwNumber! + '-' + swPartNumber.majorRelease, true);
        }

        this.swPartNumberClicked.emit(preselectedSwPartNumber);
      }
    }
  }
}
